import { Popover, PopoverContent, PopoverTrigger } from "@gettactic/components";
import { Palette, Pipette } from "lucide-react";
import React from "react";
import { HexAlphaColorPicker } from "react-colorful";
import { logError } from "../../../../helpers";
import { EditorField } from "./EditorField";

export interface ColorPickerInputProps {
  value: string;
  onChange: (value: string) => void;
  showInput?: boolean;
  paletteColors?: string[];
  trigger?: React.ReactNode;
  triggerAsChild?: boolean;
}

const classes = {
  Wrapper: "flex gap-1.5 items-center",
  ColorPickerPreview:
    "rounded-md w-7 h-7 border border-neutral-200 shrink-0 overflow-hidden",
  PickerButton:
    "flex items-center gap-1.5 rounded-md p-0.5 hover:bg-neutral-100 transition-colors",
  PickerButtonIcon: "text-neutral-500 size-4",
  PickerPopoverContent: "flex w-auto flex-col items-center p-0",
  PaletteColorsWrapper:
    "mt-2 flex w-full max-w-[300px] items-start border-t border-neutral-200 bg-neutral-50 p-2",
  PaletteColorsWrapperColors: "flex flex-1 flex-wrap gap-1.5",
  PaletteColorItem:
    "h-5 w-5 rounded border border-neutral-200 hover:scale-110 transition-transform"
};

interface EyeDropperResult {
  sRGBHex: string;
}
interface EyeDropperConstructor {
  new (): { open: () => Promise<EyeDropperResult> };
}

/** Normalize a hex value to always include alpha (8 digits). */
const toHex8 = (hex: string): string => {
  const clean = hex.replace("#", "");
  if (clean.length === 6) return `#${clean}ff`;
  if (clean.length === 8) return `#${clean}`;
  return hex;
};

export const ColorPickerInput: React.FC<ColorPickerInputProps> = ({
  value,
  onChange,
  showInput = true,
  paletteColors,
  trigger,
  triggerAsChild = true
}) => {
  const handleChangeColor = (color: string) => {
    onChange(color);
  };

  const colors = paletteColors ? paletteColors : [];

  const eyeDropperAllowed = "EyeDropper" in window;

  const pickColor = async () => {
    if (eyeDropperAllowed) {
      const eyeDropper = new (
        window as Window & { EyeDropper: EyeDropperConstructor }
      ).EyeDropper();
      try {
        const result = await eyeDropper.open();
        if (result.sRGBHex) {
          onChange?.(result.sRGBHex);
        }
      } catch (e) {
        logError(e);
      }
    } else {
      logError("EyeDropper API not supported!");
    }
  };

  // Show checkerboard behind color preview so alpha is visible
  const _previewStyle: React.CSSProperties = {
    backgroundColor: value,
    backgroundImage:
      "linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)",
    backgroundSize: "8px 8px",
    backgroundPosition: "0 0, 0 4px, 4px -4px, -4px 0"
  };

  return (
    <div className={classes.Wrapper}>
      <Popover>
        <PopoverTrigger asChild={triggerAsChild}>
          {trigger || (
            <button
              type="button"
              aria-label="Pick color"
              className={classes.PickerButton}
            >
              <div className={classes.ColorPickerPreview}>
                <div
                  className="h-full w-full rounded"
                  style={{ backgroundColor: value }}
                />
              </div>
              <Palette className={classes.PickerButtonIcon} />
            </button>
          )}
        </PopoverTrigger>
        <PopoverContent
          className={classes.PickerPopoverContent}
          align="start"
          side="top"
        >
          <HexAlphaColorPicker
            color={toHex8(value || "#000000ff")}
            onChange={handleChangeColor}
          />
          {(colors.length > 0 || eyeDropperAllowed) && (
            <div className={classes.PaletteColorsWrapper}>
              <div className={classes.PaletteColorsWrapperColors}>
                {colors.map((color) => (
                  <button
                    key={color}
                    type="button"
                    aria-label={`Use color ${color}`}
                    className={classes.PaletteColorItem}
                    style={{ backgroundColor: color }}
                    onClick={() => handleChangeColor(color)}
                  />
                ))}
              </div>
              <div>
                <div className={"w-full text-center"}>
                  <button
                    type="button"
                    aria-label="Pick color from screen"
                    className="rounded-md p-1 text-neutral-600 hover:bg-neutral-200"
                    onClick={pickColor}
                  >
                    <Pipette className={"size-4"} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>
      {showInput && (
        <EditorField
          label="Color hex value"
          containerClassName="w-24"
          value={value}
          onChange={(e) => handleChangeColor(e.target.value)}
        />
      )}
    </div>
  );
};
